import { MDBBtn, MDBCheckbox, MDBCol, MDBRow } from "mdb-react-ui-kit";
import {
    Dispatch,
    FC,
    SetStateAction,
    useMemo,
    useRef,
    useState,
    useCallback,
    useEffect,
} from "react";
import { TourOption } from "typings/mdb-react-ui-kit";
import { EFilterType, ETourType, ModalSize } from "../../models/common/enums";
import { FilterResponseTO, SearchResponseTO } from "../../models/search";
import { TagDestination } from "../../models/common/common";
import styles from "../../styles/search/Search.Search.module.scss";
import { randomUid } from "../../utils/randomIds";
import mediaControlSvg from "../../utils/svg-icon/mediaControl";
import socialAndBrandSvg from "../../utils/svg-icon/socialAndBrand";
import userInterfaceSvg from "../../utils/svg-icon/userInterface";
import contactsSvg from "../../utils/svg-icon/contacts";
import { handleArrayStateChange } from "../../utils/hooks/handleArrayStateChange";
import InfoModalContainer from "../common/info/InfoModal";
import TravelDestinationsCardModalSectionDetail from "./TravelDestionationsCardsModal";

const TourDestinationsFilters: FC<{
    selectedDestinations: string[];
    selectedActivities: string[];
    selectedPrice: number;
    selectedTourType: string[];
    tourOption: TourOption;
    setSelectedDestinations: Dispatch<SetStateAction<string[]>>;
    setSelectedActivities: Dispatch<SetStateAction<string[]>>;
    setSelectedPrice: Dispatch<SetStateAction<number>>;
    setSelectedTourType: Dispatch<SetStateAction<string[]>>;
    setTourOption: Dispatch<SetStateAction<TourOption>>;
    filtersData: FilterResponseTO;
    onResetFilters?: () => void;
    hasTagDestinationFilter?: boolean;
    selectedMinPrice?: number;
    setSelectedMinPrice?: Dispatch<SetStateAction<number>>;
    onPriceRangeChange?: (min: number, max: number) => void;
    displayMinPrice?: number;
    displayMaxPrice?: number;
    availableTours?: SearchResponseTO[];
    tagDestinations?: TagDestination[];
    selectedTagDestination?: string | null;
    setSelectedTagDestination?: Dispatch<SetStateAction<string | null>>;
}> = ({
    selectedDestinations,
    selectedActivities,
    selectedPrice,
    selectedTourType,
    tourOption,
    setSelectedDestinations,
    setSelectedActivities,
    setSelectedPrice,
    setSelectedTourType,
    setTourOption,
    filtersData,
    onResetFilters,
    hasTagDestinationFilter = false,
    selectedMinPrice,
    setSelectedMinPrice,
    onPriceRangeChange,
    displayMinPrice,
    displayMaxPrice,
    availableTours,
    tagDestinations,
    selectedTagDestination,
    setSelectedTagDestination,
}) => {
    const [showInfoModal, setShowInfoModal] = useState<boolean>(false);
    const [filterType, setFilterType] = useState<EFilterType[]>([]);
    const [internalMinPrice, setInternalMinPrice] = useState<number>(
        selectedMinPrice ?? displayMinPrice ?? +filtersData.minPrice
    );
    const sliderContainerRef = useRef<HTMLDivElement>(null);
    const minThumbRef = useRef<HTMLDivElement>(null);
    const maxThumbRef = useRef<HTMLDivElement>(null);
    const activeThumbRef = useRef<"min" | "max" | null>(null);

    // Usa il prop se disponibile, altrimenti lo stato interno
    const minPrice = selectedMinPrice ?? internalMinPrice;
    const setMinPrice = setSelectedMinPrice ?? setInternalMinPrice;

    // Usa i prezzi convertiti per la visualizzazione, altrimenti quelli di filtersData
    const minPriceDisplay = displayMinPrice ?? +filtersData.minPrice;
    const maxPriceDisplay = displayMaxPrice ?? +filtersData.maxPrice;

    // Notifica i cambiamenti al componente padre se c'è un callback
    const handleMinPriceChange = useCallback(
        (value: number): void => {
            setMinPrice(value);
            onPriceRangeChange?.(value, selectedPrice);
        },
        [setMinPrice, onPriceRangeChange, selectedPrice]
    );

    const handleMaxPriceChange = useCallback(
        (value: number): void => {
            setSelectedPrice(value);
            onPriceRangeChange?.(minPrice, value);
        },
        [setSelectedPrice, onPriceRangeChange, minPrice]
    );

    // Funzione per calcolare il valore dal click/touch
    const getValueFromPosition = useCallback(
        (clientX: number): number => {
            if (!sliderContainerRef.current) {
                return minPriceDisplay;
            }
            const rect = sliderContainerRef.current.getBoundingClientRect();
            const x = clientX - rect.left;
            const { width } = rect;
            const percentage = Math.max(0, Math.min(1, x / width));
            const range = maxPriceDisplay + 100 - minPriceDisplay;
            const value =
                Math.round((minPriceDisplay + percentage * range) / 100) * 100;
            return Math.max(
                minPriceDisplay,
                Math.min(maxPriceDisplay + 100, value)
            );
        },
        [minPriceDisplay, maxPriceDisplay]
    );

    // Gestione mouse/touch per il thumb minimo
    const handleMinThumbInteraction = useCallback(
        (clientX: number): void => {
            const newValue = getValueFromPosition(clientX);
            if (newValue <= selectedPrice) {
                handleMinPriceChange(newValue);
            } else {
                handleMinPriceChange(selectedPrice);
            }
        },
        [getValueFromPosition, selectedPrice, handleMinPriceChange]
    );

    // Gestione mouse/touch per il thumb massimo
    const handleMaxThumbInteraction = useCallback(
        (clientX: number): void => {
            const newValue = getValueFromPosition(clientX);
            if (newValue >= minPrice) {
                handleMaxPriceChange(newValue);
            } else {
                handleMaxPriceChange(minPrice);
            }
        },
        [getValueFromPosition, minPrice, handleMaxPriceChange]
    );

    // Gestione eventi globali per il drag
    const handleMove = useCallback(
        (clientX: number): void => {
            if (activeThumbRef.current === "min") {
                handleMinThumbInteraction(clientX);
            } else if (activeThumbRef.current === "max") {
                handleMaxThumbInteraction(clientX);
            }
        },
        [handleMinThumbInteraction, handleMaxThumbInteraction]
    );

    const handleEnd = useCallback(() => {
        activeThumbRef.current = null;
    }, []);

    // Setup event listeners per drag
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => handleMove(e.clientX);
        const handleMouseUp = () => handleEnd();
        const handleTouchMove = (e: TouchEvent) => {
            e.preventDefault();
            if (e.touches[0]) handleMove(e.touches[0].clientX);
        };
        const handleTouchEnd = () => handleEnd();

        if (typeof window !== "undefined") {
            window.addEventListener("mousemove", handleMouseMove);
            window.addEventListener("mouseup", handleMouseUp);
            window.addEventListener("touchmove", handleTouchMove, {
                passive: false,
            });
            window.addEventListener("touchend", handleTouchEnd);
            return () => {
                window.removeEventListener("mousemove", handleMouseMove);
                window.removeEventListener("mouseup", handleMouseUp);
                window.removeEventListener("touchmove", handleTouchMove);
                window.removeEventListener("touchend", handleTouchEnd);
            };
        }
        return undefined;
    }, [handleMove, handleEnd]);

    const tourTypeDaysMap: Record<string, string> = {
        city: "(3-5 days)",
        region: "(3-9 days)",
        italy: "(6-17 days)",
    };

    const tourTypeOrder: Record<string, number> = {
        city: 1,
        region: 2,
        italy: 3,
    };

    // Estrai le attività uniche dai tour disponibili
    // eslint-disable-next-line sonarjs/cognitive-complexity
    const availableActivities = useMemo(() => {
        if (!availableTours || availableTours.length === 0) {
            return filtersData?.activities || [];
        }

        const activitiesSet = new Set<string>();

        availableTours.forEach(tour => {
            // Estrai attività da quality
            if (tour.quality?.activities) {
                tour.quality.activities.forEach(activity => {
                    if (activity?.name) {
                        activitiesSet.add(activity.name);
                    }
                });
            }
            // Estrai attività da premium
            if (tour.premium?.activities) {
                tour.premium.activities.forEach(activity => {
                    if (activity?.name) {
                        activitiesSet.add(activity.name);
                    }
                });
            }
        });

        const availableActivitiesList = Array.from(activitiesSet);

        // Filtra la lista di attività per mostrare solo quelle presenti nei tour
        return (filtersData?.activities || []).filter(activity =>
            availableActivitiesList.includes(activity)
        );
    }, [availableTours, filtersData?.activities]);

    const isResetActive = () => {
        return (
            tourOption ||
            selectedDestinations.length > 0 ||
            selectedActivities.length > 0 ||
            selectedPrice < maxPriceDisplay ||
            minPrice > minPriceDisplay ||
            selectedTourType.length > 0 ||
            hasTagDestinationFilter
        );
    };
    const resetFiltersHandler = () => {
        setTourOption(undefined);
        setSelectedDestinations([]);
        setSelectedActivities([]);
        setSelectedPrice(maxPriceDisplay);
        setMinPrice(minPriceDisplay);
        setSelectedTourType([]);
        setSelectedTagDestination?.(null);
        onPriceRangeChange?.(minPriceDisplay, maxPriceDisplay);
        onResetFilters?.();
    };

    const handleSelectAllTours = () => {
        const allTourTypes = filtersData?.tourTypes || [];
        const allSelected =
            allTourTypes.length > 0 &&
            allTourTypes.every(tourType => selectedTourType.includes(tourType));

        if (allSelected) {
            // Deseleziona tutti
            setSelectedTourType([]);
        } else {
            // Seleziona tutti
            setSelectedTourType([...allTourTypes]);
        }
    };

    return (
        <div className="w-100">
            <div
                className={`${styles.multiSelectContainer} w-100 d-flex flex-column align-items-center p-lg-4`}
            >
                <div
                    className={styles.customSelect}
                    onClick={() =>
                        handleArrayStateChange<EFilterType>(
                            EFilterType.DESTINATIONS,
                            setFilterType
                        )
                    }
                >
                    <p>Destinations</p>
                    <span>
                        {filterType.includes(EFilterType.DESTINATIONS) ? (
                            <userInterfaceSvg.ArrowUpSimpleLine />
                        ) : (
                            <userInterfaceSvg.ArrowDownSimpleLine />
                        )}
                    </span>
                </div>
                {filterType.includes(EFilterType.DESTINATIONS) && (
                    <div className="w-100 row">
                        {tagDestinations && tagDestinations.length > 0
                            ? tagDestinations.map(tag => {
                                  const handleTagDestinationToggle =
                                      (): void => {
                                          if (!setSelectedTagDestination) {
                                              return;
                                          }

                                          const newTagDestination =
                                              selectedTagDestination ===
                                              tag.tagDestination
                                                  ? null
                                                  : tag.tagDestination;

                                          setSelectedTagDestination(
                                              newTagDestination
                                          );

                                          // Deseleziona le destinations normali quando si seleziona un tagDestination
                                          if (newTagDestination) {
                                              setSelectedDestinations([]);
                                          }
                                      };

                                  const isSelected =
                                      selectedTagDestination ===
                                      tag.tagDestination;

                                  return (
                                      <div
                                          className={`${styles.checkboxContainer} col-12`}
                                          key={tag.tagDestination}
                                      >
                                          <MDBCheckbox
                                              key={tag.tagDestination}
                                              onChange={
                                                  handleTagDestinationToggle
                                              }
                                              checked={isSelected}
                                              className={
                                                  styles.customCheckboxChecked
                                              }
                                          />
                                          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                                          <div
                                              onClick={
                                                  handleTagDestinationToggle
                                              }
                                          >
                                              <p>{tag.title}</p>
                                          </div>
                                      </div>
                                  );
                              })
                            : filtersData?.destinations?.map(el => (
                                  <div
                                      className={`${styles.checkboxContainer} col-12`}
                                      key={randomUid()}
                                  >
                                      <MDBCheckbox
                                          // label={el}
                                          key={randomUid()}
                                          onChange={() => {
                                              handleArrayStateChange<string>(
                                                  el,
                                                  setSelectedDestinations
                                              );
                                              // Deseleziona il tagDestination quando si seleziona una destination normale
                                              if (setSelectedTagDestination) {
                                                  setSelectedTagDestination(
                                                      null
                                                  );
                                              }
                                          }}
                                          checked={selectedDestinations.includes(
                                              el
                                          )}
                                          className={
                                              styles.customCheckboxChecked
                                          }
                                      />
                                      <div
                                          onClick={() => {
                                              handleArrayStateChange<string>(
                                                  el,
                                                  setSelectedDestinations
                                              );
                                              // Deseleziona il tagDestination quando si seleziona una destination normale
                                              if (setSelectedTagDestination) {
                                                  setSelectedTagDestination(
                                                      null
                                                  );
                                              }
                                          }}
                                      >
                                          <p>{el}</p>
                                      </div>
                                  </div>
                              ))}
                    </div>
                )}
                <div
                    className={styles.customSelect}
                    onClick={() =>
                        handleArrayStateChange<EFilterType>(
                            EFilterType.PRICE,
                            setFilterType
                        )
                    }
                >
                    <p>Price range</p>
                    <span>
                        {filterType.includes(EFilterType.PRICE) ? (
                            <userInterfaceSvg.ArrowUpSimpleLine />
                        ) : (
                            <userInterfaceSvg.ArrowDownSimpleLine />
                        )}
                    </span>
                </div>
                {filterType.includes(EFilterType.PRICE) && (
                    <div className="w-100 row">
                        <div className="d-flex justify-content-between gap-3 mb-3">
                            <div className="flex-fill">
                                <label
                                    htmlFor="price-from-input-desktop"
                                    style={{
                                        display: "block",
                                        marginBottom: "0.5rem",
                                    }}
                                >
                                    <span
                                        style={{
                                            fontSize: "10px",
                                            fontWeight: 500,
                                            color: "#B1B5C3",
                                            display: "block",
                                        }}
                                    >
                                        FROM
                                    </span>
                                    <input
                                        id="price-from-input-desktop"
                                        type="number"
                                        min={minPriceDisplay}
                                        max={selectedPrice}
                                        step="100"
                                        value={minPrice}
                                        onChange={e => {
                                            const value = Number(
                                                e.target.value
                                            );
                                            // Permetti qualsiasi valore durante la digitazione
                                            if (!Number.isNaN(value)) {
                                                handleMinPriceChange(value);
                                            }
                                        }}
                                        onBlur={e => {
                                            // Quando l'utente esce dal campo, valida e applica il valore
                                            const value = Number(
                                                e.target.value
                                            );
                                            if (
                                                Number.isNaN(value) ||
                                                value < minPriceDisplay
                                            ) {
                                                handleMinPriceChange(
                                                    minPriceDisplay
                                                );
                                            } else if (value > selectedPrice) {
                                                handleMinPriceChange(
                                                    selectedPrice
                                                );
                                            } else {
                                                // Assicurati che il valore venga applicato anche se non è cambiato
                                                handleMinPriceChange(value);
                                            }
                                        }}
                                        onKeyDown={e => {
                                            // Quando l'utente preme Enter, applica il filtro
                                            if (e.key === "Enter") {
                                                e.currentTarget.blur();
                                            }
                                        }}
                                        className={`form-control ${styles.priceInput}`}
                                        style={{
                                            borderRadius: "8px",
                                            border: "1px solid #dee2e6",
                                            padding: "0.5rem",
                                        }}
                                    />
                                </label>
                            </div>
                            <div className="flex-fill">
                                <label
                                    htmlFor="price-to-input-desktop"
                                    style={{
                                        display: "block",
                                        marginBottom: "0.5rem",
                                    }}
                                >
                                    <span
                                        style={{
                                            fontSize: "10px",
                                            fontWeight: 500,
                                            color: "#B1B5C3",
                                            display: "block",
                                        }}
                                    >
                                        TO
                                    </span>
                                    <input
                                        id="price-to-input-desktop"
                                        type="number"
                                        min={minPrice}
                                        max={maxPriceDisplay + 100}
                                        step="100"
                                        value={selectedPrice}
                                        onChange={e => {
                                            const value = Number(
                                                e.target.value
                                            );
                                            // Limita il valore anche durante la digitazione per evitare sfasamenti
                                            if (!Number.isNaN(value)) {
                                                const limitedValue = Math.min(
                                                    Math.max(value, minPrice),
                                                    maxPriceDisplay + 100
                                                );
                                                handleMaxPriceChange(
                                                    limitedValue
                                                );
                                            }
                                        }}
                                        onBlur={e => {
                                            // Quando l'utente esce dal campo, valida e applica il valore
                                            const value = Number(
                                                e.target.value
                                            );
                                            if (
                                                Number.isNaN(value) ||
                                                value < minPrice
                                            ) {
                                                handleMaxPriceChange(minPrice);
                                            } else if (
                                                value >
                                                maxPriceDisplay + 100
                                            ) {
                                                handleMaxPriceChange(
                                                    maxPriceDisplay + 100
                                                );
                                            } else if (
                                                value <
                                                (selectedMinPrice ??
                                                    minPriceDisplay)
                                            ) {
                                                // Se il max è minore del min, imposta il max uguale al min
                                                handleMaxPriceChange(
                                                    selectedMinPrice ??
                                                        minPriceDisplay
                                                );
                                            } else {
                                                // Assicurati che il valore venga applicato anche se non è cambiato
                                                handleMaxPriceChange(value);
                                            }
                                        }}
                                        onKeyDown={e => {
                                            // Quando l'utente preme Enter, applica il filtro
                                            if (e.key === "Enter") {
                                                e.currentTarget.blur();
                                            }
                                        }}
                                        className={`form-control ${styles.priceInput}`}
                                        style={{
                                            borderRadius: "8px",
                                            border: "1px solid #dee2e6",
                                            padding: "0.5rem",
                                        }}
                                    />
                                </label>
                            </div>
                        </div>
                        <div
                            ref={sliderContainerRef}
                            className="position-relative"
                            style={{
                                padding: "1.5rem 0",
                                minHeight: "50px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "100%",
                                touchAction: "none",
                                WebkitTouchCallout: "none",
                                WebkitUserSelect: "none",
                                userSelect: "none",
                                overflow: "visible",
                            }}
                            onMouseDown={e => {
                                if (!sliderContainerRef.current) return;
                                const rect =
                                    sliderContainerRef.current.getBoundingClientRect();
                                const clickX = e.clientX - rect.left;
                                const { width } = rect;
                                const clickPercent = (clickX / width) * 100;

                                const minPercent =
                                    ((minPrice - minPriceDisplay) /
                                        (maxPriceDisplay +
                                            100 -
                                            minPriceDisplay)) *
                                    100;
                                const maxPercent =
                                    ((selectedPrice - minPriceDisplay) /
                                        (maxPriceDisplay +
                                            100 -
                                            minPriceDisplay)) *
                                    100;

                                const distanceToMin = Math.abs(
                                    clickPercent - minPercent
                                );
                                const distanceToMax = Math.abs(
                                    clickPercent - maxPercent
                                );

                                if (distanceToMin < distanceToMax) {
                                    activeThumbRef.current = "min";
                                    handleMinThumbInteraction(e.clientX);
                                } else {
                                    activeThumbRef.current = "max";
                                    handleMaxThumbInteraction(e.clientX);
                                }
                            }}
                            onTouchStart={e => {
                                e.preventDefault();
                                if (
                                    !sliderContainerRef.current ||
                                    !e.touches[0]
                                )
                                    return;
                                const rect =
                                    sliderContainerRef.current.getBoundingClientRect();
                                const clickX = e.touches[0].clientX - rect.left;
                                const { width } = rect;
                                const clickPercent = (clickX / width) * 100;

                                const minPercent =
                                    ((minPrice - minPriceDisplay) /
                                        (maxPriceDisplay +
                                            100 -
                                            minPriceDisplay)) *
                                    100;
                                const maxPercent =
                                    ((selectedPrice - minPriceDisplay) /
                                        (maxPriceDisplay +
                                            100 -
                                            minPriceDisplay)) *
                                    100;

                                const distanceToMin = Math.abs(
                                    clickPercent - minPercent
                                );
                                const distanceToMax = Math.abs(
                                    clickPercent - maxPercent
                                );

                                if (distanceToMin < distanceToMax) {
                                    activeThumbRef.current = "min";
                                    handleMinThumbInteraction(
                                        e.touches[0].clientX
                                    );
                                } else {
                                    activeThumbRef.current = "max";
                                    handleMaxThumbInteraction(
                                        e.touches[0].clientX
                                    );
                                }
                            }}
                        >
                            {/* Track grigio di sfondo */}
                            <div
                                style={{
                                    position: "absolute",
                                    width: "100%",
                                    height: "6px",
                                    backgroundColor: "#B1B5C3",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    pointerEvents: "none",
                                    zIndex: 0,
                                    borderRadius: "8px",
                                }}
                            />
                            {/* Track rosso attivo */}
                            <div
                                style={{
                                    position: "absolute",
                                    height: "10px",
                                    backgroundColor: "#ED6F6F",
                                    left: `${
                                        ((minPrice - minPriceDisplay) /
                                            (maxPriceDisplay +
                                                100 -
                                                minPriceDisplay)) *
                                        100
                                    }%`,
                                    width: `${
                                        ((selectedPrice - minPrice) /
                                            (maxPriceDisplay +
                                                100 -
                                                minPriceDisplay)) *
                                        100
                                    }%`,
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    pointerEvents: "none",
                                    zIndex: 1,
                                    borderRadius: "8px",
                                }}
                            />
                            {/* Thumb minimo */}
                            <div
                                ref={minThumbRef}
                                className={styles.customThumb}
                                style={{
                                    position: "absolute",
                                    left: `${
                                        ((minPrice - minPriceDisplay) /
                                            (maxPriceDisplay +
                                                100 -
                                                minPriceDisplay)) *
                                        100
                                    }%`,
                                    top: "50%",
                                    transform: "translate(-50%, -50%)",
                                    width: "24px",
                                    height: "24px",
                                    borderRadius: "50%",
                                    backgroundColor: "#ED6F6F",
                                    border: "3px solid white",
                                    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.3)",
                                    cursor: "grab",
                                    zIndex: 4,
                                    touchAction: "none",
                                    WebkitTapHighlightColor: "transparent",
                                    transition: "transform 0.1s ease",
                                }}
                                onMouseDown={e => {
                                    e.stopPropagation();
                                    activeThumbRef.current = "min";
                                }}
                                onTouchStart={e => {
                                    e.stopPropagation();
                                    activeThumbRef.current = "min";
                                }}
                            />
                            {/* Thumb massimo */}
                            <div
                                ref={maxThumbRef}
                                className={styles.customThumb}
                                style={{
                                    position: "absolute",
                                    left: `${
                                        ((selectedPrice - minPriceDisplay) /
                                            (maxPriceDisplay +
                                                100 -
                                                minPriceDisplay)) *
                                        100
                                    }%`,
                                    top: "50%",
                                    transform: "translate(-50%, -50%)",
                                    width: "24px",
                                    height: "24px",
                                    borderRadius: "50%",
                                    backgroundColor: "#ED6F6F",
                                    border: "3px solid white",
                                    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.3)",
                                    cursor: "grab",
                                    zIndex: 5,
                                    touchAction: "none",
                                    WebkitTapHighlightColor: "transparent",
                                    transition: "transform 0.1s ease",
                                }}
                                onMouseDown={e => {
                                    e.stopPropagation();
                                    activeThumbRef.current = "max";
                                }}
                                onTouchStart={e => {
                                    e.stopPropagation();
                                    activeThumbRef.current = "max";
                                }}
                            />
                        </div>
                    </div>
                )}
                <div
                    className={styles.customSelect}
                    onClick={() =>
                        handleArrayStateChange<EFilterType>(
                            EFilterType.ACTIVITIES,
                            setFilterType
                        )
                    }
                >
                    <p>Activities</p>
                    <span>
                        {filterType.includes(EFilterType.ACTIVITIES) ? (
                            <userInterfaceSvg.ArrowUpSimpleLine />
                        ) : (
                            <userInterfaceSvg.ArrowDownSimpleLine />
                        )}
                    </span>
                </div>
                {filterType.includes(EFilterType.ACTIVITIES) && (
                    <div className="w-100 row ">
                        <div className={styles.activitiesContainer}>
                            {availableActivities.map(el => (
                                <span
                                    onClick={() =>
                                        handleArrayStateChange<string>(
                                            el,
                                            setSelectedActivities
                                        )
                                    }
                                    className={
                                        selectedActivities.includes(el)
                                            ? styles.customActivitiesSelected
                                            : styles.customActivities
                                    }
                                    key={randomUid()}
                                >
                                    {el}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
                <div
                    className={styles.customSelect}
                    onClick={() =>
                        handleArrayStateChange<EFilterType>(
                            EFilterType.TOURTYPE,
                            setFilterType
                        )
                    }
                >
                    <p>Tours</p>
                    <span>
                        {filterType.includes(EFilterType.TOURTYPE) ? (
                            <userInterfaceSvg.ArrowUpSimpleLine />
                        ) : (
                            <userInterfaceSvg.ArrowDownSimpleLine />
                        )}
                    </span>
                </div>
                {filterType.includes(EFilterType.TOURTYPE) && (
                    <div className="w-100">
                        {filtersData?.tourTypes
                            ?.slice()
                            .sort(
                                (a, b) =>
                                    (tourTypeOrder[a] || 999) -
                                    (tourTypeOrder[b] || 999)
                            )
                            .map(el => (
                                <div
                                    className={styles.checkboxContainer}
                                    key={randomUid()}
                                >
                                    <MDBCheckbox
                                        name={`${
                                            ETourType[
                                                el as keyof typeof ETourType
                                            ]
                                        }`}
                                        // label={`${
                                        //     ETourType[el as keyof typeof ETourType]
                                        // }`}
                                        checked={selectedTourType.includes(el)}
                                        key={randomUid()}
                                        onChange={() => {
                                            handleArrayStateChange<string>(
                                                el,
                                                setSelectedTourType
                                            );
                                        }}
                                        className={styles.customCheckboxChecked}
                                    />
                                    <div
                                        onClick={() => {
                                            handleArrayStateChange<string>(
                                                el,
                                                setSelectedTourType
                                            );
                                        }}
                                    >
                                        <p>
                                            {
                                                ETourType[
                                                    el as keyof typeof ETourType
                                                ]
                                            }{" "}
                                            {tourTypeDaysMap[el] && (
                                                <span>
                                                    {tourTypeDaysMap[el]}
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        {filtersData?.tourTypes &&
                            filtersData.tourTypes.length > 0 && (
                                <div
                                    className={styles.checkboxContainer}
                                    key={randomUid()}
                                >
                                    <MDBCheckbox
                                        name="all-tours"
                                        checked={
                                            filtersData.tourTypes.length > 0 &&
                                            filtersData.tourTypes.every(
                                                tourType =>
                                                    selectedTourType.includes(
                                                        tourType
                                                    )
                                            )
                                        }
                                        key={randomUid()}
                                        onChange={handleSelectAllTours}
                                        className={styles.customCheckboxChecked}
                                    />
                                    <div onClick={handleSelectAllTours}>
                                        <p>All tours</p>
                                    </div>
                                </div>
                            )}
                    </div>
                )}
                <div className={styles.typeOfTourContainer}>
                    <MDBRow>
                        <MDBCol size={10} lg={9} xxl={10}>
                            <p>Experience</p>
                        </MDBCol>
                        <MDBCol
                            className="d-inline-flex justify-content-end"
                            onClick={() => setShowInfoModal(true)}
                        >
                            <MDBBtn
                                floating
                                className={` ${styles.infoButton} text-center shadow-0 text-black fw-bold p-0`}
                            >
                                <contactsSvg.InfoCircleLine
                                    style={{
                                        width: "24px",
                                        height: "24px",
                                    }}
                                />
                            </MDBBtn>
                        </MDBCol>
                    </MDBRow>

                    <div className="row">
                        <div className="d-flex justify-content-between flex-column flex-lg-row gap-3">
                            <MDBBtn
                                className={
                                    tourOption === "quality"
                                        ? styles.typeOfTourButtonSelected
                                        : styles.typeOfTourButton
                                }
                                onClick={() => setTourOption("quality")}
                            >
                                <span className="ps-2">Quality</span>
                                <div className="ps-2 pt-1">
                                    <userInterfaceSvg.StarLine />
                                </div>
                            </MDBBtn>
                            <MDBBtn
                                className={
                                    tourOption === "premium"
                                        ? styles.typeOfTourButtonSelected
                                        : styles.typeOfTourButton
                                }
                                onClick={() => setTourOption("premium")}
                            >
                                <span className="ps-2">Premium</span>
                                <div className="ps-2 pt-1">
                                    <socialAndBrandSvg.SketchLine />
                                </div>
                            </MDBBtn>
                        </div>
                    </div>
                </div>
                {isResetActive() && (
                    <div className="d-flex w-100 justify-content-center justify-content-lg-start">
                        <div className={styles.filterResetButton}>
                            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
                            <div
                                className="pt-1"
                                onClick={() => resetFiltersHandler()}
                            >
                                <mediaControlSvg.CloseCircleFilled />
                            </div>
                            <span onClick={() => resetFiltersHandler()}>
                                Reset filter
                            </span>
                        </div>
                    </div>
                )}
            </div>
            {/* eslint-disable react/no-children-prop */}
            <InfoModalContainer
                title=""
                children={<TravelDestinationsCardModalSectionDetail />}
                show={showInfoModal}
                setShow={setShowInfoModal}
                size={ModalSize.LG}
            />
        </div>
    );
};

export default TourDestinationsFilters;
